import { fomaAll } from "../../fsm/rules/fomaAll";
import { compileRuleSet } from "../../rules/compileRuleSet";
import { SegmentStrategy } from "../../rules/intoSegments";
import { Rule } from "../../rules/rule";
import { CompiledRuleSet, RuleSet } from "../../rules/ruleSet";
import { FailedRule, RulesRunner } from "../../rules/rulesRunner";
import { ApplyRulesResult, runRulesOnIpa } from "../../rules/runRules";
import { pieRules } from "../../ruleSets/pieRules";
import { pieToIpa } from "../../ruleSets/pieToIpa";
import { wordsToIpa } from "../../transcribe/wordsToIpa";

/** either scrub the input of extra chars, or lookup English words & return PIE */
export function cleanOrLookupPie(
  input: string, option: 'cleanLaryngeal' | 'cleanSystem' | 'lookup'
): { result: string, error: string } {
  if (option === 'cleanLaryngeal') {
    if (cacheCleanLaryngeal === null) {
      cacheCleanLaryngeal = new RulesRunner({ preprocess: ['cleanPunctuation'], inputSegments: SegmentStrategy.Single, rules: stressPhonemeRules });
    }
    const results = cacheCleanLaryngeal.runRulesOnIpa(input);
    return getResultError(results);
  } else if (option === 'cleanSystem') {
    if (cacheCleanSystem === null) {
      cacheCleanSystem = new RulesRunner({ preprocess: ['cleanPunctuation'], inputSegments: SegmentStrategy.Single, rules: removePIEMarkers });
    }
    const results = cacheCleanSystem.runRulesOnIpa(input);
    return getResultError(results);
  } else {
    const [result, error] = wordsToIpa(input, 'en_PIE');
    return { result, error: error.join(' ') };
  }
}
let cacheCleanLaryngeal: RulesRunner | null = null;
let cacheCleanSystem: RulesRunner | null = null;

const stressPhonemeRules: Rule[] = [
  { from: 'á', to: 'ˈa' },
  { from: 'é', to: 'ˈe' }, { from: 'ḗ', to: 'ˈeː' },
  { from: 'í', to: 'ˈi' },
  { from: 'ó', to: 'ˈo' }, { from: 'ṓ', to: 'ˈoː' },
  { from: 'ú', to: 'ˈu' }, { from: 'ṹ', to: 'ˈuː' },
  { from: 'ý', to: 'ˈy' },
  { from: 'ḱ', to: 'ˈk' }, { from: 'ḿ', to: 'ˈm"' },
];
const removePIEMarkers: Rule[] = [
  { from: '·', to: '' },
  { from: '-', to: '' },
]

/** run a given rule on PIE input */
export function runPieRules(
  input: string, ruleType: 'laryngeal' | 'system', ruleName: string,
): { result: string, error: string } {
  return cacheRules.run(input, ruleType, ruleName);
}

/** convert PIE-specific symbols to standard IPA */
export function convertPieToIpa(input: string): { result: string, error: string } {
  if (cachePieToIpa === null) {
    cachePieToIpa = new RulesRunner(pieToIpa);
  }
  const results = cachePieToIpa.runRulesOnIpa(input);
  return getResultError(results);
}
let cachePieToIpa: RulesRunner | null = null;

function getResultError(response: ApplyRulesResult): { result: string, error: string } {
  const errors = !response.hasUnknowns ? [] : response.hasUnknowns.map(p => p.phoneme);
  const error = errors.join(' ');
  return response.ipa ? { result: response.ipa, error } : { result: '', error };
}

/** cache the compiled version of PIE rules & run them */
class CacheRules {
  private rules: Record<string, CompiledRuleSet> = {};
  private errors: Record<string, FailedRule[]> = {};

  /** run the specified rule, caching the compiled rule if this is the first time it was run */
  run(
    input: string, ruleType: 'laryngeal' | 'system', ruleName: string
  ): { result: string, error: string } {
    const compiled = this.getCompiledRule(ruleType, ruleName);
    if (typeof compiled === 'string') {
      return { result: '', error: compiled };
    }
    const response = runRulesOnIpa(input, compiled);
    return getResultError(response);
  }

  private getCompiledRule(ruleType: 'laryngeal' | 'system', ruleName: string): CompiledRuleSet | string {
    const name = ruleType + '-' + ruleName;
    // abort if there were errors in this rule
    if (this.errors[name]) {
      return 'internal rule error';
    }

    // return it if already compiled
    const rule = this.rules[name];
    if (rule) {
      return rule;
    }

    // lookup rule & compile it
    const rawrule: RuleSet = ruleType === 'laryngeal'
      ? pieRules[ruleName]
      : { inputSegments: SegmentStrategy.Single, outputSegments: SegmentStrategy.Single, fsm: fomaAll[ruleName] };
    if (!rawrule) {
      return 'rule not found';
    }
    const [compiled, errors] = compileRuleSet(rawrule);
    if (errors.length > 0) {
      this.errors[name] = errors;
      return 'internal rule error';
    }
    this.rules[name] = compiled;
    return compiled;
  }
}
let cacheRules: CacheRules = new CacheRules();
